/**
 * 路径 1.5 — §37 storage bridge
 * Dongniai Scene Validator
 *
 * Background: BrowserDocumentStore from @openmaic/storage validates scenes
 * via the DSL's validateScene (which only accepts 'slide' / 'quiz'). Our app
 * widens Scene with 'interactive' / 'pbl' content kinds (see lib/types/stage.ts
 * AppSceneContent = DslSceneContent | InteractiveContent | PBLContent) plus
 * dongniai business fields (outlineId / lessonIndex / progress / etc.). The
 * store's README §49-53 explicitly supports this — pass a custom SceneValidator
 * that also accepts the widened kinds so the validation gate stays fail-loud
 * for OUR shapes rather than silently rejecting them.
 *
 * Design choice: minimal validator — only enforce that:
 *   1. id is a string
 *   2. stageId is a string
 *   3. order is a finite number
 * (matches BrowserDocumentStore's assertStorableScene, kept here for clarity)
 *
 * We do NOT deeply validate content shape here — the DSL gate already covers
 * slide/quiz, and interactive/pbl content is opaque app-owned data (see
 * storage/README §49-53). Deep validation would duplicate work the store
 * itself does, and risk rejecting legacy data the DSL gate already accepted.
 */
import type { SceneValidator } from '@openmaic/storage';

export const dongniaiSceneValidator: SceneValidator = (scene) => {
  if (typeof scene !== 'object' || scene === null) {
    return { valid: false, errors: [{ path: '', message: 'scene must be an object' }] };
  }
  const s = scene as Record<string, unknown>;
  const errors: { path: string; message: string }[] = [];

  if (typeof s.id !== 'string') {
    errors.push({ path: 'id', message: `must be string, got ${typeof s.id}` });
  }
  if (typeof s.stageId !== 'string') {
    errors.push({ path: 'stageId', message: `must be string, got ${typeof s.stageId}` });
  }
  if (typeof s.order !== 'number' || !Number.isFinite(s.order)) {
    errors.push({ path: 'order', message: `must be finite number, got ${String(s.order)}` });
  }

  return errors.length === 0
    ? { valid: true }
    : { valid: false, errors };
};